import { api, uploadProgress } from "@/api/client";
import { saveBlobResponse } from "@/lib/download";

/** Приёмка поставок: заказы поставщику, сессия с фото-черновиками, финиш. */

export type ReceivingStatus = "none" | "in_progress" | "completed";

export type PhotoCategory = "upd" | "certificates" | "goods";

/** Порядок и русские имена категорий — как на бэке (`receiving/constants.py`). */
export const PHOTO_CATEGORIES: PhotoCategory[] = ["upd", "certificates", "goods"];
export const PHOTO_CATEGORY_TITLES: Record<PhotoCategory, string> = {
  upd: "УПД",
  certificates: "Сертификаты",
  goods: "Товар и маркировка",
};

export interface RefName {
  ref: string;
  name: string;
}

/** Фактическая приёмка товара по регистру 1С `ЗаказыПоставщикам`:
 *  full — всё принято, partial — часть, none — ничего/заказ не проведён. */
export type GoodsStatus = "full" | "partial" | "none";

export interface GoodsReceipt {
  status: GoodsStatus;
  lines_total: number;
  lines_received: number;
}

export interface SupplierOrderCard {
  ref: string;
  number: string;
  date: string;
  /** `ДатаПоступления` шапки; пусто, если дата ведётся в строках заказа. */
  receiving_date: string;
  organization_ref: string;
  organization_name: string;
  kontragent_ref: string;
  kontragent_name: string;
  state_ref: string;
  state_name: string;
  posted: boolean;
  receiving_status: ReceivingStatus;
  goods: GoodsReceipt;
}

export interface OrderListResponse {
  items: SupplierOrderCard[];
  has_more: boolean;
}

export interface OrderLineView {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  /** Количество КАК В ЗАКАЗЕ (в единице строки), цен в разделе нет. */
  qty: number;
  unit_name: string;
  unit_ref: string;
  unit_type: string;
  unit_coef: number;
  /** Не принято по строке, В ЕДИНИЦЕ строки (0 — принята целиком). */
  qty_remaining: number | null;
  /** Партия учётная (ингредиенты): номер поставщика вводят руками. */
  partia_tracked: boolean;
}

export interface OrderDetail {
  header: SupplierOrderCard;
  lines: OrderLineView[];
}

export interface PhotoMeta {
  id: string;
  category: PhotoCategory;
  created_at: string;
  uploaded: boolean;
  /** К чему привязан кадр: партия (паспорт качества) или место (этикетка). */
  batch_id: string | null;
  place_id: string | null;
}

export interface ReceivingSession {
  id: string;
  status: "open" | "completed";
  order_ref: string;
  order_number: string;
  order_date: string | null;
  receiving_date: string | null;
  organization_name: string | null;
  kontragent_name: string | null;
  completed_at: string | null;
  photos: PhotoMeta[];
}

export interface FinishResult {
  photos_attached: number;
  photos_total: number;
  /** Поставленные задачи проверки: {"lab": 123, "qc": 124}. */
  bitrix_tasks?: Record<string, number>;
  /** Куда, кому и под каким номером — для плашки «на проверку: …». */
  bitrix_task_info?: {
    target: string;
    target_title: string;
    task_id: number;
    responsible: string;
    created: boolean;
  }[];
  /** Почему задачи не поставились. Приёмку это не роняет. */
  bitrix_warning?: string;
}

export interface OrderListParams {
  /** Поиск по номеру заказа ИЛИ по названию поставщика (по частям слов). */
  q?: string;
  organization_ref?: string;
  state_ref?: string;
  date_from?: string;
  date_to?: string;
  /** Фильтр приёмки товара по регистру 1С. */
  receipt?: GoodsStatus | "";
  skip?: number;
}

function listParams(params: OrderListParams) {
  return {
    q: params.q || undefined,
    organization_ref: params.organization_ref || undefined,
    state_ref: params.state_ref || undefined,
    date_from: params.date_from || undefined,
    date_to: params.date_to || undefined,
    receipt: params.receipt || undefined,
    skip: params.skip || undefined,
  };
}

export async function listReceivingOrders(
  params: OrderListParams,
): Promise<OrderListResponse> {
  const { data } = await api.get<OrderListResponse>("/receiving/orders", {
    params: listParams(params),
  });
  return data;
}

export async function listReceivingOrganizations(): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/receiving/organizations");
  return data;
}

export async function listReceivingOrderStates(): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/receiving/order-states");
  return data;
}

export async function getReceivingOrder(orderRef: string): Promise<OrderDetail> {
  const { data } = await api.get<OrderDetail>(`/receiving/orders/${orderRef}`);
  return data;
}

/** Открыть (или продолжить) приёмку заказа — get_or_create, идемпотентно. */
export async function openReceivingSession(orderRef: string): Promise<ReceivingSession> {
  const { data } = await api.post<ReceivingSession>("/receiving/sessions", {
    order_ref: orderRef,
  });
  return data;
}

export async function getReceivingSession(id: string): Promise<ReceivingSession> {
  const { data } = await api.get<ReceivingSession>(`/receiving/sessions/${id}`);
  return data;
}

/** Снять фото → черновик сессии (в 1С уходит пакетом при финише). */
export async function stageReceivingPhoto(
  sessionId: string,
  category: PhotoCategory,
  blob: Blob,
): Promise<PhotoMeta> {
  const form = new FormData();
  form.append("file", blob, `${category}.jpg`);
  const { data } = await api.post<PhotoMeta>(
    `/receiving/sessions/${sessionId}/photos`,
    form,
    {
      params: { category },
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data;
}

/** Картинка фото-черновика (auth — Bearer, поэтому через axios) → object URL.
 *  Вызывающий обязан `URL.revokeObjectURL` при размонтировании. */
export async function fetchReceivingPhotoUrl(
  sessionId: string,
  photoId: string,
): Promise<string> {
  const resp = await api.get<Blob>(
    `/receiving/sessions/${sessionId}/photos/${photoId}`,
    { responseType: "blob" },
  );
  return URL.createObjectURL(resp.data);
}

export async function deleteReceivingPhoto(
  sessionId: string,
  photoId: string,
): Promise<void> {
  await api.delete(`/receiving/sessions/${sessionId}/photos/${photoId}`);
}

/** Выгрузить фото вложениями в заказ поставщику и завершить приёмку.
 *  При ошибке 1С отправленное зафиксировано — повтор дошлёт остаток. */
export async function finishReceiving(sessionId: string): Promise<FinishResult> {
  const { data } = await api.post<FinishResult>(
    `/receiving/sessions/${sessionId}/finish`,
  );
  return data;
}

export async function reopenReceiving(sessionId: string): Promise<ReceivingSession> {
  const { data } = await api.post<ReceivingSession>(
    `/receiving/sessions/${sessionId}/reopen`,
  );
  return data;
}

// ── Журнал приёмки (read-only, право receiving_review) ────────────────────

export interface ReceivingSummary {
  status: ReceivingStatus;
  photo_counts: Record<PhotoCategory, number>;
  completed_at: string | null;
  received_by: string;
}

/** Строка журнала — ПРИХОД (накладная), а не заказ: по одному заказу
 *  приезжает несколько машин, и склеенная строка прятала, что приняли этой. */
export interface ReviewDeliveryCard {
  id: string;
  order_ref: string;
  order_number: string;
  incoming_number: string;
  incoming_date: string | null;
  upd_missing?: boolean;
  organization_name: string | null;
  kontragent_name: string | null;
  receipt_number: string | null;
  receipt_posted: boolean;
  status: "open" | "completed";
  created_at: string | null;
  completed_at: string | null;
  received_by: string;
  positions: number;
  places: number;
  photo_counts: Record<PhotoCategory, number>;
  /** Проверка ОКК прихода; `null` — проверки нет. */
  okk: OkkStatus | null;
}

export interface ReviewListResponse {
  items: ReviewDeliveryCard[];
  has_more: boolean;
}

export interface ReviewPhoto {
  id: string;
  category: PhotoCategory;
  created_at: string;
}

export interface ReviewDeliveryDetail {
  header: ReviewDeliveryCard;
  batches: BatchView[];
  photos: ReviewPhoto[];
}

export interface ReviewListParams {
  q?: string;
  organization_ref?: string;
  delivery_status?: "open" | "completed" | "";
  date_from?: string;
  date_to?: string;
  skip?: number;
}

export async function listReviewDeliveries(
  params: ReviewListParams,
): Promise<ReviewListResponse> {
  const { data } = await api.get<ReviewListResponse>("/receiving-review/deliveries", {
    params: {
      q: params.q || undefined,
      organization_ref: params.organization_ref || undefined,
      delivery_status: params.delivery_status || undefined,
      date_from: params.date_from || undefined,
      date_to: params.date_to || undefined,
      skip: params.skip || undefined,
    },
  });
  return data;
}

/** Скачать фото прихода архивом (вся приёмка или одна категория). */
export async function downloadReviewPhotosZip(
  deliveryId: string,
  category?: PhotoCategory,
): Promise<void> {
  const resp = await api.get<Blob>(
    `/receiving-review/deliveries/${deliveryId}/photos.zip`,
    { params: { category: category || undefined }, responseType: "blob" },
  );
  saveBlobResponse(resp, category ? `${category}.zip` : "photos.zip");
}

export async function listReviewOrganizations(): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/receiving-review/organizations");
  return data;
}

export async function getReviewDelivery(
  deliveryId: string,
): Promise<ReviewDeliveryDetail> {
  const { data } = await api.get<ReviewDeliveryDetail>(
    `/receiving-review/deliveries/${deliveryId}`,
  );
  return data;
}

/** Картинка фото (auth — Bearer) → object URL; вызывающий делает revoke. */
export async function fetchReviewPhotoUrl(photoId: string): Promise<string> {
  const resp = await api.get<Blob>(`/receiving-review/photos/${photoId}`, {
    responseType: "blob",
  });
  return URL.createObjectURL(resp.data);
}

// ── Приход (УПД), партии, тарные места ────────────────────────────────────
//
// Единица приёмки — ПОСТАВКА, а не заказ: один заказ приезжает несколькими
// машинами, и раньше вторая просто не заводилась.

export interface PlaceView {
  id: string;
  seq: number;
  place_type: string;
  qty_declared: number | null;
  gross_kg: number | null;
  net_kg: number | null;
  label_printed: boolean;
  has_photo: boolean;
}

/** `accounting` — учётная партия (остатки, FEFO), `label` — «ярлык поставки»:
 *  партия заведена и напечатана, но в остатках её нет и в приходную она не
 *  попадает. Признак берётся с КАТЕГОРИИ номенклатуры в 1С. */
export type BatchRole = "accounting" | "label";

export interface BatchView {
  id: string;
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_name: string;
  supplier_batch: string;
  production_date: string | null;
  expiry_date: string | null;
  qty: number;
  unit_name: string;
  role: BatchRole;
  partia_number: string | null;
  barcode: string | null;
  places: PlaceView[];
}

/** Что изменилось в снимке номенклатуры после перечитывания из 1С. */
export interface SnapshotRefreshResult {
  changed: string[];
}

export interface DeliveryView {
  id: string;
  status: "open" | "completed";
  order_ref: string;
  order_number: string;
  incoming_number: string;
  incoming_date: string | null;
  upd_missing?: boolean;
  organization_name: string | null;
  kontragent_name: string | null;
  kontragent_ref: string | null;
  completed_at: string | null;
  receipt_number: string | null;
  receipt_posted: boolean;
  batches: BatchView[];
  photos: PhotoMeta[];
  /** Мест без фото — ПОДСКАЗКА, а не запрет: фото необязательно. */
  places_missing_photo: number;
  /** Проверка ОКК прихода; `null` — проверки нет (не завершён или нет позиций ОКК). */
  okk: OkkStatus | null;
}

/** Статус проверки ОКК для бейджа в приёмке (раздел `okk_receiving`). */
export interface OkkStatus {
  inspection_id: string;
  status: string;
}

export interface ScanResponse {
  raw: string;
  kind: "gs1" | "ean13" | "unknown";
  supplier_batch: string;
  production_date: string | null;
  expiry_date: string | null;
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  /** supplier_binding | one_c_registry | "" (код незнакомый). */
  matched_by: string;
  /** Что именно запомнится за позицией: у GS1 — GTIN, а не строка с партией. */
  binding_code: string;
  /** Партия учётная — номер УПД в поле партии поставщика не подставляем. */
  partia_tracked: boolean;
}

export interface ReceivingConfig {
  scan_method: "tsd" | "camera" | "both";
  printer_ready: boolean;
  /** Макет этикетки партии заполнен — условие, независимое от принтера. */
  label_template_ready: boolean;
}

export interface BatchCreate {
  nomenclature_ref: string;
  characteristic_ref?: string;
  characteristic_name?: string;
  /** Единица СТРОКИ ЗАКАЗА. Без неё приходная накладная уходит без
   *  `ЕдиницаИзмерения`, и 1С приходует короба как штуки. */
  unit_ref?: string | null;
  unit_type?: string | null;
  unit_coef?: number;
  supplier_batch?: string;
  production_date?: string | null;
  expiry_date?: string | null;
  qty: number;
  unit_name?: string;
  place_type?: string;
  places: number;
  qty_per_place?: number | null;
}

export interface FinishDeliveryResult {
  photos_attached: number;
  photos_total: number;
  receipt_ref: string;
  receipt_number: string;
  /** Задачи на проверку: куда, кому и под каким номером. */
  bitrix_task_info?: {
    target: string;
    target_title: string;
    task_id: number;
    responsible: string;
    created: boolean;
  }[];
  /** Почему задачи не поставились. Приёмку это НЕ роняет. */
  bitrix_warning?: string;
}

export async function getReceivingConfig(): Promise<ReceivingConfig> {
  const { data } = await api.get<ReceivingConfig>("/receiving/config");
  return data;
}

export async function listOrderDeliveries(orderRef: string): Promise<DeliveryView[]> {
  const { data } = await api.get<DeliveryView[]>(
    `/receiving/orders/${orderRef}/deliveries`,
  );
  return data;
}

export async function listOpenDeliveries(): Promise<DeliveryView[]> {
  const { data } = await api.get<DeliveryView[]>("/receiving/deliveries/open");
  return data;
}

export async function createDelivery(body: {
  order_ref: string;
  incoming_number: string;
  incoming_date?: string | null;
  /** Машина без документов: номер подставит портал, настоящий впишут позже. */
  upd_missing?: boolean;
}): Promise<DeliveryView> {
  const { data } = await api.post<DeliveryView>("/receiving/deliveries", body);
  return data;
}

export async function getDelivery(id: string): Promise<DeliveryView> {
  const { data } = await api.get<DeliveryView>(`/receiving/deliveries/${id}`);
  return data;
}

export async function deleteDelivery(id: string): Promise<{ warnings: string[] }> {
  const { data } = await api.delete<{ warnings: string[] }>(
    `/receiving/deliveries/${id}`,
  );
  return data;
}

export async function acceptBatch(
  deliveryId: string,
  body: BatchCreate,
): Promise<BatchView> {
  const { data } = await api.post<BatchView>(
    `/receiving/deliveries/${deliveryId}/batches`,
    body,
  );
  return data;
}

export async function deleteBatch(
  deliveryId: string,
  batchId: string,
): Promise<{ warnings: string[] }> {
  const { data } = await api.delete<{ warnings: string[] }>(
    `/receiving/deliveries/${deliveryId}/batches/${batchId}`,
  );
  return data;
}

/** Напечатать этикетки партии.
 *
 *  `count` — сколько наклеек нужно физически. Не задан — по одной на каждое
 *  тарное место (прежнее поведение). Экран подставляет в поле именно число
 *  мест, а человек правит его руками: одно «место» на складе часто означает
 *  всю партию, а коробок в ней сорок пять. */
export async function printBatchLabels(
  deliveryId: string,
  batchId: string,
  count?: number,
): Promise<{ printed: number }> {
  const { data } = await api.post<{ printed: number }>(
    `/receiving/deliveries/${deliveryId}/batches/${batchId}/labels`,
    count ? { count } : {},
  );
  return data;
}

/** Перечитать снимок номенклатуры партии из 1С.
 *
 *  Снимок (имя, артикул, характеристика, категория) пишется при приёме, и
 *  дозаполненный в 1С позже артикул сам в него не попадает. `changed` —
 *  русские подписи изменившихся полей; пустой массив значит, что в 1С то же
 *  самое. */
export async function refreshBatchSnapshot(
  deliveryId: string,
  batchId: string,
): Promise<SnapshotRefreshResult> {
  const { data } = await api.post<SnapshotRefreshResult>(
    `/receiving/deliveries/${deliveryId}/batches/${batchId}/refresh-1c`,
  );
  return data;
}

export async function scanCode(
  deliveryId: string,
  code: string,
): Promise<ScanResponse> {
  const { data } = await api.post<ScanResponse>("/receiving/scan", {
    delivery_id: deliveryId,
    code,
  });
  return data;
}

export async function bindBarcode(body: {
  delivery_id: string;
  code: string;
  nomenclature_ref: string;
  nomenclature_name?: string;
  characteristic_ref?: string;
  characteristic_name?: string;
}): Promise<void> {
  await api.post("/receiving/barcodes", body);
}

export async function uploadDeliveryPhoto(
  deliveryId: string,
  file: Blob,
  opts: {
    category: PhotoCategory;
    batchId?: string;
    placeId?: string;
    /**
     * Процент отправки. ⚠️ Нужен на складском вайфае: снимок теперь ~2 МБ
     * (полное разрешение матрицы), и без прогресса «идёт загрузка» и
     * «зависло» выглядят одинаково — кладовщик снимает заново и получает
     * пачку одинаковых фото.
     */
    onProgress?: (percent: number) => void;
  },
): Promise<PhotoMeta> {
  const form = new FormData();
  form.append("file", file, "photo.jpg");
  const { data } = await api.post<PhotoMeta>(
    `/receiving/deliveries/${deliveryId}/photos`,
    form,
    {
      params: {
        category: opts.category,
        batch_id: opts.batchId || undefined,
        place_id: opts.placeId || undefined,
      },
      ...uploadProgress(opts.onProgress),
    },
  );
  return data;
}

export async function finishDelivery(id: string): Promise<FinishDeliveryResult> {
  const { data } = await api.post<FinishDeliveryResult>(
    `/receiving/deliveries/${id}/finish`,
  );
  return data;
}

export async function postDeliveryReceipt(id: string): Promise<DeliveryView> {
  const { data } = await api.post<DeliveryView>(`/receiving/deliveries/${id}/post`);
  return data;
}

export async function reopenDelivery(id: string): Promise<DeliveryView> {
  const { data } = await api.post<DeliveryView>(`/receiving/deliveries/${id}/reopen`);
  return data;
}

/** Картинка фото прихода → object URL.
 *
 *  Через axios, а не прямым `<img src>`: ручка под Bearer-авторизацией, и
 *  тег картинки токен не пошлёт. Вызывающий обязан `URL.revokeObjectURL`.
 *
 *  Миниатюры нужны не для красоты: без них кладовщик не видит, что съёмка
 *  идёт, и щёлкает вслепую — 80 снимков одного УПД (18.08.2026). */
export async function fetchDeliveryPhotoUrl(
  deliveryId: string,
  photoId: string,
): Promise<string> {
  const resp = await api.get<Blob>(
    `/receiving/deliveries/${deliveryId}/photos/${photoId}`,
    { responseType: "blob" },
  );
  return URL.createObjectURL(resp.data);
}

export async function deleteDeliveryPhoto(
  deliveryId: string,
  photoId: string,
): Promise<void> {
  await api.delete(`/receiving/deliveries/${deliveryId}/photos/${photoId}`);
}


/** Повторить постановку задач на проверку: ставятся только недостающие. */
export async function retryInspectionTasks(deliveryId: string): Promise<FinishResult> {
  const { data } = await api.post<FinishResult>(
    `/receiving/deliveries/${deliveryId}/inspection-tasks`,
  );
  return data;
}

export interface UpdSetResult {
  delivery: DeliveryView;
  warnings: string[];
}

/** Вписать настоящий номер УПД в приход, принятый без документов.
 *
 * Правит разом ключ прихода, номер партии поставщика у непартионных позиций,
 * реквизит приходной накладной в 1С (в том числе проведённой) и тексты задач
 * проверки в Битриксе. Возвращает то, что портал поправить не смог.
 */
export async function setDeliveryUpd(
  deliveryId: string,
  body: { incoming_number: string; incoming_date?: string | null },
): Promise<UpdSetResult> {
  const { data } = await api.post<UpdSetResult>(
    `/receiving/deliveries/${deliveryId}/upd`,
    body,
  );
  return data;
}
